import { useEffect, useState } from 'react'
import { BuildingElementSubType } from '../../../../models/Construction'
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { ListShowMoreIcon } from '../../../Svgs'
import { useDispatch, useSelector } from 'react-redux'
import {
  createWall,
  CreateWallPayload,
  setSelectedTool,
  setSelectedToolProps,
} from '../../../../actions/construction'
import store, { RootState } from '../../../../store'
import { SceneManager } from '../../../../visualization/sceneManager'
import { EventType } from '../../../../utils/EventEmitter'
import { createSelector } from '@reduxjs/toolkit'
import { updateHistory } from '../../../../actions/history'
import { ActionType } from '../../../../reducers/history'

const allowedWallTypes = [
  BuildingElementSubType.Exterior,
  BuildingElementSubType.Interior,
]

const allowedWallThicknesses = [3.5, 5.5]

const convertDataSelector = createSelector(
  [(state: RootState) => state.construction],
  (construction) => construction.convertData,
)

const CreateWall = () => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(true)
  const wallType = useSelector(
    (state: RootState) => state.construction.selectedToolProps.wallType,
  )
  const wallThickness = useSelector(
    (state: RootState) => state.construction.selectedToolProps.wallThickness,
  )

  useEffect(() => {
    const eventEmitter = SceneManager.getInstance().getEventEmitter()
    if (!eventEmitter) {
      return
    }

    const handleNewWallCreation = (createWallPayload: CreateWallPayload) => {
      dispatch(createWall(createWallPayload))

      const convertData = structuredClone(convertDataSelector(store.getState()))
      dispatch(
        updateHistory({
          convertData,
          items: [
            {
              itemId: createWallPayload.id,
              actionType: ActionType.ADD,
            },
          ],
        }),
      )
      dispatch(setSelectedTool(null))
    }

    eventEmitter.on(EventType.BuildingAddWallElement, handleNewWallCreation)

    return () => {
      eventEmitter.off(EventType.BuildingAddWallElement, handleNewWallCreation)
    }
  }, [dispatch])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '4px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          color='#04002E'
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Create a New Wall Settings
        </Typography>

        <IconButton
          onClick={() => setIsOpen((prev) => !prev)}
          sx={{
            transition: 'transform 0.3s',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(180deg)',
          }}
        >
          <ListShowMoreIcon
            sx={{
              fontSize: '14px',
              color: '#797979',
            }}
          />
        </IconButton>
      </Box>

      <Divider
        flexItem
        orientation='horizontal'
        sx={{
          backgroundColor: '#E4E4E4',
        }}
      />

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <Stack
          direction='column'
          sx={{
            padding: '8px 12px',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Typography
              sx={{
                color: '#737373',

                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textAlign: 'left',
              }}
            >
              Wall Type
            </Typography>

            <ToggleButtonGroup
              size='small'
              color='primary'
              value={wallType}
              exclusive
              aria-label='Platform'
              sx={{
                flex: 1,
                borderRadius: '4px',
                backgroundColor: '#F2F2F2',
                '.MuiToggleButton-root': {
                  margin: 0,
                  border: 'none',
                  borderRadius: '4px',
                },
              }}
            >
              {allowedWallTypes.map((type) => (
                <ToggleButton
                  key={type}
                  value={type}
                  onClick={() =>
                    dispatch(setSelectedToolProps({ wallType: type }))
                  }
                  sx={{
                    width: '100%',
                    backgroundColor: '#F5F5F5',
                    '&:hover': {
                      backgroundColor: '#E5F0FF',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#1705E5',
                      '&:hover': {
                        backgroundColor: '#1705E5',
                      },
                      '.MuiTypography-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',

                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal',
                      textTransform: 'capitalize',
                      color: '#97999B',
                    }}
                  >
                    {type.toLowerCase()}
                  </Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Typography
              sx={{
                color: '#737373',

                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textAlign: 'left',
              }}
            >
              Wall Thickness
            </Typography>

            <ToggleButtonGroup
              size='small'
              color='primary'
              value={wallThickness}
              exclusive
              aria-label='Platform'
              sx={{
                flex: 1,
                borderRadius: '4px',
                backgroundColor: '#F2F2F2',
                '.MuiToggleButton-root': {
                  margin: 0,
                  border: 'none',
                  borderRadius: '4px',
                },
              }}
            >
              {allowedWallThicknesses.map((thickness) => (
                <ToggleButton
                  key={thickness}
                  value={thickness}
                  onClick={() =>
                    dispatch(setSelectedToolProps({ wallThickness: thickness }))
                  }
                  sx={{
                    width: '100%',
                    backgroundColor: '#F5F5F5',
                    '&:hover': {
                      backgroundColor: '#E5F0FF',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#1705E5',
                      '&:hover': {
                        backgroundColor: '#1705E5',
                      },
                      '.MuiTypography-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',

                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal',
                      textTransform: 'lowercase',
                      color: '#97999B',
                    }}
                  >
                    {thickness} in
                  </Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Stack>

        <Divider
          flexItem
          orientation='horizontal'
          sx={{
            backgroundColor: '#E4E4E4',
          }}
        />
      </Collapse>
    </Box>
  )
}

export default CreateWall
